import { Beaker, Info } from 'lucide-react';
import { amountForTank, formatAmount } from '@/utils/sprayDose';

export interface TankDoseProduct {
  key: string | number;
  name: string;
  /** The dose actually applied, in `rateUnit` — any concentration already in it. */
  rate: number | null;
  rateUnit: string;
}

interface TankDoseTableProps {
  products: TankDoseProduct[];
  tankCapacityL: number | null | undefined;
  outputRateLpha: number | null | undefined;
  /** Water for the whole job, so a part-filled last tank can be stated too. */
  totalWaterL: number | null;
}

/**
 * What goes into each tank, product by product.
 *
 * This is the number somebody stands at the tank with. A job total and a count
 * of fills leaves them doing the division in the shed, and a mix partner had
 * no amount at all. The last tank is stated separately when it is a part fill,
 * because a full-tank dose in half a tank of water is double strength.
 */
function TankDoseTable({ products, tankCapacityL, outputRateLpha, totalWaterL }: TankDoseTableProps) {
  if (!products.length || !tankCapacityL || tankCapacityL <= 0) return null;

  const fills = totalWaterL && totalWaterL > 0 ? Math.ceil(totalWaterL / tankCapacityL) : null;
  const lastWater = fills && totalWaterL ? totalWaterL - (fills - 1) * tankCapacityL : null;
  // Within a litre of full is a full tank; nobody fills to the litre.
  const partialLast = fills != null && lastWater != null && tankCapacityL - lastWater >= 1 ? lastWater : null;
  const fullTanks = fills == null ? null : partialLast != null ? fills - 1 : fills;

  const rows = products.map(p => ({
    ...p,
    full: amountForTank({ rate: p.rate, rateUnit: p.rateUnit, waterL: tankCapacityL, outputRateLpha }),
    last: partialLast != null
      ? amountForTank({ rate: p.rate, rateUnit: p.rateUnit, waterL: partialLast, outputRateLpha })
      : null,
  }));
  const anyUnknown = rows.some(r => r.full == null);

  return (
    <div className="rounded-xl border border-green-200 bg-white p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Beaker className="h-4 w-4 text-green-700 shrink-0" />
        <h3 className="text-sm font-semibold text-neutral-800">What goes in each tank</h3>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 text-sm">
        <p className="text-xs text-neutral-500">
          Full tank — {Math.round(tankCapacityL)} L water
          {fullTanks != null && fullTanks > 0 && ` (${fullTanks}×)`}
        </p>
        <span />
        {rows.map(r => (
          <div key={r.key} className="contents">
            <p className="text-neutral-700 break-words min-w-0">{r.name}</p>
            <p className={`text-right font-semibold ${r.full ? 'text-green-800' : 'text-neutral-400'}`}>
              {r.full ? formatAmount(r.full.amount, r.full.unit) : '—'}
            </p>
          </div>
        ))}
      </div>

      {partialLast != null && (
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 text-sm pt-2 border-t border-neutral-100">
          <p className="text-xs text-neutral-500">Last tank — {Math.round(partialLast)} L water</p>
          <span />
          {rows.map(r => (
            <div key={r.key} className="contents">
              <p className="text-neutral-700 break-words min-w-0">{r.name}</p>
              <p className={`text-right font-semibold ${r.last ? 'text-green-800' : 'text-neutral-400'}`}>
                {r.last ? formatAmount(r.last.amount, r.last.unit) : '—'}
              </p>
            </div>
          ))}
        </div>
      )}

      {anyUnknown && (
        <p className="text-xs text-amber-700 flex items-start gap-1">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {outputRateLpha
            ? 'A product with no rate set has no amount — give it a rate.'
            : 'A per-hectare rate needs the sprayer\'s calibrated output rate (L/ha) to turn a tank of water into an amount of product. Calibrate the sprayer to fill these in.'}
        </p>
      )}
    </div>
  );
}

export default TankDoseTable;
